<template>
  <v-col
    cols="12"
    class="stationary-sidebar"
    :style="{
      'padding-top': padding + 'px',
    }"
  >
    <SidebarDesktop
      v-if="$vuetify.display.mdAndUp"
      :get-progress-prop="getProgress"
      :include-spouse-prop="includeSpouse"
      :formatted-current-coverage-prop="formattedCurrentCoverage"
      :formatted-need-prop="formattedNeed"
      :additional-need-prop="additionalNeed"
      :formatted-additional-prop="formattedAdditional"
      @updateSelectedTab="updateSelectedTabValue"
      @contact-rep="contactRep = true"
      :display-contact-rep="hasReps"
    />
    <SidebarMobile
      v-if="$vuetify.display.smAndDown"
      :get-progress-prop="getProgress"
      :include-spouse-prop="includeSpouse"
      :formatted-current-coverage-prop="formattedCurrentCoverage"
      :formatted-need-prop="formattedNeed"
      :additional-need-prop="additionalNeed"
      :formatted-additional-prop="formattedAdditional"
      @updateSelectedTab="updateSelectedTabValue"
      @contact-rep="contactRep = true"
      :display-contact-rep="hasReps"
    />
    <MyRep :rep-page="false" />
  </v-col>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { mapState, mapStores } from 'pinia';
import { useDimeguidanceStore } from '@/modules/dimeguidance/store/dimeguidance.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { useGlobalStore } from '@/stores/global/global.store';
import ContactRep from '@/components/ContactRep/ContactRep.vue';
import MyRep from '@/modules/myRep/MyRep.vue';
import SidebarDesktop from './SidebarDesktop.vue';
import SidebarMobile from './SidebarMobile.vue';

const formatNumber = (number: number) =>
  Intl.NumberFormat('en-US', {
    style: 'decimal',
  }).format(number);

const componentOptions: ComponentOptions = {
  name: 'Sidebar',
  components: {
    SidebarDesktop,
    SidebarMobile,
    ContactRep,
    MyRep,
  },
  props: {
    currentCoverage: {
      type: Object,
      required: true,
      default: () => ({
        you: 0,
        spouse: 0,
      }),
    },
  },
  data: () => ({
    padding: 0,
    activeSidebarTab: 0,
    sidebarElement: null,
    scrollListener: undefined,
    topOffset: undefined,
    maxOffset: undefined,
    selectedTab: 0,
    contactRep: false,
  }),
  computed: {
    ...mapStores(useDimeguidanceStore),
    ...mapState(useDimeguidanceStore, {
      includeSpouse: 'includeSpouse',
      dimeCalculation: 'dimeCalculation',
    }),
    ...mapState(usePolicyStore, ['policiesSortedByDate']),
    ...mapState(useCustomerStore, ['investmentsSortedByDate', 'customerProfile']),
    ...mapState(useGlobalStore, ['getAgent', 'agents']),
    sender() {
      return {
        email: this.customerProfile?.email || '',
        phone: this.customerProfile?.mobilePhone || '',
        firstName: this.customerProfile.firstName,
        lastName: this.customerProfile.lastName,
      };
    },
    hasReps() {
      // This is being done to test out another Contact Rep that is being added for new design.
      return this.agents?.length > 0;
    },
    currentCoverageAmount() {
      return this.selectedTab === 0 ? this.currentCoverage.you : this.currentCoverage.spouse;
    },
    formattedCurrentCoverage() {
      return formatNumber(this.currentCoverageAmount);
    },
    formattedNeed() {
      return formatNumber(
        this.selectedTab === 0
          ? this.dimeCalculation.you.totalProtectionNeeded
          : this.dimeCalculation.spouse.totalProtectionNeeded
      );
    },
    additionalNeed() {
      return (
        (this.selectedTab === 0
          ? this.dimeCalculation.you.totalProtectionNeeded
          : this.dimeCalculation.spouse.totalProtectionNeeded) - this.currentCoverageAmount
      );
    },
    formattedAdditional() {
      return this.additionalNeed > 0
        ? formatNumber(Math.ceil(this.additionalNeed / 1000) * 1000)
        : 0;
    },
    getProgress() {
      if (this.currentCoverageAmount > 0) {
        return (
          (this.currentCoverageAmount /
            (this.selectedTab === 0
              ? this.dimeCalculation.you.totalProtectionNeeded
              : this.dimeCalculation.spouse.totalProtectionNeeded)) *
          100
        );
      }
      return 0;
    },
  },
  methods: {
    resizeCallback() {
      this.topOffset = undefined;
    },
    updateSelectedTabValue(value: number) {
      this.selectedTab = value;
    },
  },
  beforeDestroy() {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener);
    }
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss">
.footer-wrapper {
  @media only screen and (min-width: 1023px) {
    display: none !important;
  }
}
</style>
